import { handbookHref } from "@/lib/help-center-seo";
import { BRAND_HOME_ARIA_LABEL, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { ld } from "@/lib/theme/light-dark";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactNode } from "react";
import { WebsiteThemeToggle } from "../../../website-theme";
import { ProductMenu } from "../../product-menu/product-menu";
import { Logo } from "../hero-primitives";
import { H } from "../hero-tokens";
import { OPEN_POSITIONS_COUNT } from "../open-positions-count";

/**
 * SECTION 1 — Hero top nav bar (1:1 with the prototype, lines 29–45).
 *
 * Container max-width 1240, height 64. Three flex groups:
 *  - left: Logo lockup (30px square + triangle + "hausverwalter" wordmark)
 *  - center: Produkt + KI mega-menus + Preise (/preise) + Docs (docs host) + Jobs
 *  - right: Anmelden (/login) + outlined "Erstgespräch buchen" button (/demo)
 */
export function HeroNav(): ReactNode {
  const t = useTranslations("marketing.heroNav");
  return (
    <nav className="mkhero-nav">
      {/* Left — logo lockup */}
      <Link
        href="/"
        className="mkhero-nav-logo"
        aria-label={BRAND_HOME_ARIA_LABEL}
        title={BRAND_LOGO_TITLE}
        style={{ display: "inline-flex", textDecoration: "none" }}
      >
        <Logo size={30} />
      </Link>

      {/* Center — marketing links (hidden on narrow tablet via CSS) */}
      <div className="mkhero-nav-center">
        <ProductMenu />
        <ProductMenu kind="ai" />
        <Link
          href="/preise"
          className="mkhero-nav-link"
          style={{
            fontSize: 14.5,
            textDecoration: "none",
          }}
        >
          {t("preise")}
        </Link>
        <a
          href={handbookHref()}
          className="mkhero-nav-link"
          style={{
            fontSize: 14.5,
            textDecoration: "none",
          }}
        >
          {t("handbuch")}
        </a>
        <Link
          href="/careers"
          className="mkhero-nav-link"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 14.5,
            textDecoration: "none",
          }}
        >
          {t("karriere")}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 17,
              height: 17,
              padding: "0 5px",
              borderRadius: 999,
              background: H.accent,
              color: ld("#fff", "#181b1f"),
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {OPEN_POSITIONS_COUNT}
          </span>
        </Link>
      </div>

      {/* Right — auth + primary CTA (never shrinks / clips) */}
      <div className="mkhero-nav-actions">
        <WebsiteThemeToggle />
        <Link
          href="/login"
          className="mkhero-nav-login"
          style={{
            fontSize: 14.5,
            fontWeight: 500,
            textDecoration: "none",
          }}
        >
          {t("anmelden")}
        </Link>
        <Link
          href="/demo"
          className="mkhero-ctabtn mkhero-nav-demo"
          style={{
            background: H.ctaGradient,
            color: "#fff",
            fontSize: 14.5,
            fontWeight: 600,
            padding: "9px 17px",
            borderRadius: 10,
            textDecoration: "none",
          }}
        >
          {t("demoCallBuchen")}
        </Link>
      </div>
    </nav>
  );
}
